import { tick } from 'svelte';
import type { z } from 'zod';

/**
 * Forms with the shared Zod contracts, behaving like the React site's React Hook Form setup
 * (mode `onSubmit`, re-validation `onChange`, focus on the first error):
 * - nothing is checked until the first submit;
 * - a submit checks the whole form, shows the first message of each invalid field and focuses the
 *   first invalid control in the form;
 * - after that, each edited field is checked again as it changes (only its own message moves);
 * - a valid submit hands the parsed output (Zod transforms applied) to the handler.
 */
export interface Form<S extends z.ZodType> {
  /** The values, bound to the controls (`bind:value={form.values.phone}`). */
  readonly values: z.input<S>;
  readonly errors: Record<string, string | undefined>;
  readonly submitted: boolean;
  /** Re-checks one field after it changed (call from the control's input/change event). */
  touch(name: string): void;
  /** `<form onsubmit={form.submit(onValid)}>`. */
  submit(onValid: (data: z.output<S>) => void): (e: SubmitEvent) => void;
  /** Back to the defaults (or to `next`), with no errors and not submitted. */
  reset(next?: z.input<S>): void;
  /** A message from the server for one field. */
  setError(name: string, message: string | undefined): void;
}

function firstErrors(error: z.ZodError): Record<string, string> {
  const out: Record<string, string> = {};
  for (const issue of error.issues) {
    const key = issue.path.join('.');
    if (!(key in out)) out[key] = issue.message;
  }
  return out;
}

export interface FormOptions<S extends z.ZodType> {
  /**
   * What the schema sees, from what the controls hold (React Hook Form's `setValueAs`): e.g. an
   * emptied optional text field sent as null.
   */
  prepare?: (values: z.input<S>) => z.input<S>;
}

export function createForm<S extends z.ZodType>(
  schema: () => S,
  defaults: z.input<S>,
  options: FormOptions<S> = {},
): Form<S> {
  const values = $state<z.input<S>>(structuredClone(defaults));
  let errors = $state<Record<string, string | undefined>>({});
  let submitted = $state(false);
  const parse = () => {
    const snapshot = $state.snapshot(values) as z.input<S>;
    return schema().safeParse(options.prepare ? options.prepare(snapshot) : snapshot);
  };

  return {
    get values() {
      return values;
    },
    get errors() {
      return errors;
    },
    get submitted() {
      return submitted;
    },
    touch(name) {
      if (!submitted) return;
      const result = parse();
      const message = result.success ? undefined : firstErrors(result.error)[name];
      errors = { ...errors, [name]: message };
    },
    submit(onValid) {
      return (e) => {
        e.preventDefault();
        submitted = true;
        const result = parse();
        if (!result.success) {
          errors = firstErrors(result.error);
          const form = e.currentTarget as HTMLFormElement;
          void tick().then(() => form.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus());
          return;
        }
        errors = {};
        onValid(result.data);
      };
    },
    reset(next) {
      const fresh = structuredClone(next ?? defaults) as Record<string, unknown>;
      for (const key of Object.keys(values as Record<string, unknown>)) delete (values as Record<string, unknown>)[key];
      Object.assign(values as Record<string, unknown>, fresh);
      errors = {};
      submitted = false;
    },
    setError(name, message) {
      errors = { ...errors, [name]: message };
    },
  };
}
